import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';
import { FolderCrumb, IFilesBrowserItem } from './files-browser.utils';

export const ROOT_FOLDER_ID = 0;

export type MoveTarget = {
  id: string | number;
  name: string;
  isOnPath: boolean;
  isCurrent: boolean;
  isDisabled: boolean;
  children: MoveTarget[];
};

type MoveTargetsOptions = {
  foldersByParent: Map<string, IFilesBrowserItem[]>;
  folderPath: FolderCrumb[];
  rootLabel: string;
  canMoveToRoot: boolean;
  movedItem: IFilesBrowserItem;
};

export const isMovableItem = (item?: Partial<IFilesBrowserItem>) => {
  if (!item || item.isRecognisedData || item.restricted) {
    return false;
  }
  return item.entityType === HierarchyLevelType.Node || item.entityType === HierarchyLevelType.Object;
};

export const isMoveTargetFolder = (item?: Partial<IFilesBrowserItem>) => {
  return item?.entityType === HierarchyLevelType.Node && !item.isRecognisedData && !item.restricted;
};

export const groupFoldersByParent = (items: IFilesBrowserItem[]) => {
  const foldersByParent = new Map<string, IFilesBrowserItem[]>();
  items.filter(isMoveTargetFolder).forEach((folder) => {
    const siblings = foldersByParent.get(String(folder.parent)) ?? [];
    siblings.push(folder);
    foldersByParent.set(String(folder.parent), siblings);
  });
  return foldersByParent;
};

// folder can't be moved into itself, so it's left out of the tree together with its subfolders
export const toMoveTargets = ({
  foldersByParent, folderPath, rootLabel, canMoveToRoot, movedItem,
}: MoveTargetsOptions): MoveTarget[] => {
  const pathIds = new Set(folderPath.map((crumb) => crumb.id));
  const currentFolderId = String(folderPath.at(-1)?.id ?? ROOT_FOLDER_ID);

  const toTarget = (folder: IFilesBrowserItem): MoveTarget => {
    const isCurrent = String(folder.id) === currentFolderId;
    return {
      id: folder.id,
      name: folder.rawName,
      isOnPath: pathIds.has(String(folder.id)),
      isCurrent,
      isDisabled: isCurrent,
      children: collectFolders(folder.id),
    };
  };

  const collectFolders = (parentId: string | number): MoveTarget[] => {
    const folders = foldersByParent.get(String(parentId)) ?? [];
    return folders.filter((folder) => folder.id !== movedItem.id).map(toTarget);
  };

  const isRootCurrent = currentFolderId === String(ROOT_FOLDER_ID);
  const root: MoveTarget = {
    id: ROOT_FOLDER_ID,
    name: rootLabel,
    isOnPath: true,
    isCurrent: isRootCurrent,
    isDisabled: isRootCurrent || !canMoveToRoot,
    children: [],
  };
  return [root, ...collectFolders(ROOT_FOLDER_ID)];
};

export const hasAvailableTarget = (targets: MoveTarget[]): boolean => {
  return targets.some((target) => !target.isDisabled || hasAvailableTarget(target.children));
};

export const insertMoveToAction = (actions: any[] | undefined, moveToAction: any) => {
  return (actions ?? []).map((action) => {
    if (!action.subActions) {
      return action;
    }
    const subActions = [...action.subActions];
    const deleteIndex = subActions.findIndex((subAction) => subAction.icon === 'delete');
    const insertIndex = deleteIndex === -1 ? subActions.length : deleteIndex;
    subActions.splice(insertIndex, 0, moveToAction);
    return { ...action, subActions };
  });
};
